import PropTypes from 'prop-types';
import { Suspense } from 'react';
import { StickyContainer } from 'react-sticky';

import AutoRelatedSlider from 'Component/AutoRelatedSlider';
import CmsBlock from 'Component/CmsBlock';
import ContentWrapper from 'Component/ContentWrapper';
import Loader from 'Component/Loader';
import MobilePopup from 'Component/MobilePopup';
import Popup from 'Component/Popup';
import ProductActions from 'Component/ProductActions';
import ProductReviewForm from 'Component/ProductReviewForm';
import { REVIEW_POPUP_ID } from 'Component/ProductReviews/ProductReviews.config';
import ProductTabs from 'Component/ProductTabs';
import { GROUP_01, GROUP_02 } from 'Component/ProductTabs/ProductTabs.config';
import TextPlaceholder from 'Component/TextPlaceholder';
import TypographyHeader from 'Component/TypographyHeader';
import NoMatchHandler from 'Route/NoMatchHandler';
import {
    PRODUCT_ATTRIBUTES,
    PRODUCT_INFORMATION,
    PRODUCT_INSTALLMENT,
    PRODUCT_REVIEWS,
    STATIC_SECTION_CMS_BLOCK_ID,
} from 'Route/ProductPage/ProductPage.config';
import {
    ProductAttributes,
    ProductGallery as SourceProductGallery,
    ProductInformation,
    ProductPage as SourceProductPage,
    ProductReviews,
} from 'SourceRoute/ProductPage/ProductPage.component';

import './ProductPage.style';

export { ProductInformation, ProductReviews, ProductAttributes };

export const ProductGallery = SourceProductGallery;

/** @namespace Advox/Route/ProductPage/Component */
export class ProductPage extends SourceProductPage {
    static propTypes = {
        ...SourceProductPage.propTypes,
        showAlternativeDesign: PropTypes.bool.isRequired,
    };

    tabMap = {
        [PRODUCT_INFORMATION]: {
            tabKey: 'ProductInformation',
            name: __('Description'),
            group: GROUP_01,
            shouldTabRender: () => {
                const { isInformationTabEmpty } = this.props;

                return !isInformationTabEmpty;
            },
            render: (key) => this.renderProductInformationTab(key),
        },
        [PRODUCT_ATTRIBUTES]: {
            tabKey: 'ProductAttributes',
            name: __('Technical details'),
            group: GROUP_01,
            shouldTabRender: () => {
                const { isAttributesTabEmpty } = this.props;

                return !isAttributesTabEmpty;
            },
            render: (key) => this.renderProductAttributesTab(key),
        },
        [PRODUCT_REVIEWS]: {
            tabKey: 'ProductReviews',
            name: __('Reviews'),
            group: GROUP_02,
            // Return true since we always show 'Add review' button
            shouldTabRender: () => true,
            render: (key) => this.renderProductReviewsTab(key),
        },
        [PRODUCT_INSTALLMENT]: {
            tabKey: 'ProductInstallment',
            name: __('Installment'),
            group: GROUP_02,
            shouldTabRender: () => true,
            render: (key) => this.renderProductInstallmentTab(key),
        },
    };

    renderReviewPopup() {
        const { dataSource, isMobile, isTablet, isPopupOpened, hidePopup } = this.props;

        return isMobile || isTablet ? (
            <MobilePopup title={__('Write a review')} isOpen={isPopupOpened} handleMobilePopup={hidePopup}>
                <ProductReviewForm product={dataSource} />
            </MobilePopup>
        ) : (
            <Popup id={REVIEW_POPUP_ID} mix={{ block: 'ProductReviews', elem: 'Popup' }}>
                <ProductReviewForm product={dataSource} />
            </Popup>
        );
    }

    renderProductInformationTab(key) {
        const { dataSource, parameters, areDetailsLoaded } = this.props;

        return (
            <Suspense fallback={<Loader />} key={key}>
                <ProductInformation
                    product={{ ...dataSource, parameters }}
                    areDetailsLoaded={areDetailsLoaded}
                    key={key}
                    name={key}
                />
            </Suspense>
        );
    }

    renderProductAttributesTab(key) {
        const { activeProduct, areDetailsLoaded } = this.props;

        return (
            <Suspense fallback={<Loader />} key={key}>
                <ProductAttributes product={activeProduct} areDetailsLoaded={areDetailsLoaded} key={key} name={key} />
            </Suspense>
        );
    }

    renderProductReviewsTab(key) {
        const { dataSource, areDetailsLoaded } = this.props;

        return (
            <Suspense fallback={<Loader />} key={key}>
                <ProductReviews product={dataSource} areDetailsLoaded={areDetailsLoaded} key={key} name={key} />
            </Suspense>
        );
    }

    renderProductInstallmentTab() {
        return null;
    }

    renderNameMobile() {
        const {
            activeProduct: { name },
            isMobile,
            isTablet,
        } = this.props;

        if (!isMobile && !isTablet) {
            return null;
        }

        return (
            <TypographyHeader variant="normal" mix={{ block: 'ProductActions', elem: 'Title' }} itemProp="name">
                <TextPlaceholder content={name} length="medium" />
            </TypographyHeader>
        );
    }

    renderProductPageContent() {
        const {
            getLink,
            dataSource,
            areDetailsLoaded,
            activeProduct,
            setActiveProduct,
            useEmptyGallerySwitcher,
            parameters,
            isVariant,
            showAlternativeDesign,
        } = this.props;

        return (
            <>
                {this.renderNameMobile()}
                <Suspense fallback={<Loader />}>
                    <ProductGallery
                        product={activeProduct}
                        areDetailsLoaded={areDetailsLoaded}
                        isWithEmptySwitcher={useEmptyGallerySwitcher}
                        showLoader={isVariant}
                        showAlternativeDesign={showAlternativeDesign}
                    />
                </Suspense>
                <ProductActions
                    getLink={getLink}
                    product={dataSource}
                    parameters={parameters}
                    areDetailsLoaded={areDetailsLoaded}
                    setActiveProduct={setActiveProduct}
                    showAlternativeDesign={showAlternativeDesign}
                />
            </>
        );
    }

    renderStaticSection() {
        return <CmsBlock identifier={STATIC_SECTION_CMS_BLOCK_ID} />;
    }

    renderProductTabs(withHeader, group) {
        return <ProductTabs tabs={this.shouldTabsRender()} withHeader={withHeader} group={group} />;
    }

    renderAutoRelatedSection() {
        const {
            activeProduct: { sku },
        } = this.props;

        return sku ? (
            <div block="ProductPage" elem="RelatedSlider">
                <AutoRelatedSlider type="mpARPProductPage" sku={sku} title={__('Proposed products')} />
            </div>
        ) : null;
    }

    wrapWithStickyContainer(content) {
        const { showAlternativeDesign } = this.props;

        if (showAlternativeDesign) {
            return <StickyContainer id="StickyContainer">{content}</StickyContainer>;
        }

        return content;
    }

    render() {
        return (
            <NoMatchHandler>
                <main block="ProductPage" aria-label="Product page" itemScope itemType="http://schema.org/Product">
                    {this.wrapWithStickyContainer(
                        <>
                            <ContentWrapper
                                wrapperMix={{ block: 'ProductPage', elem: 'Wrapper' }}
                                label={__('Main product details')}
                            >
                                {this.renderProductPageContent()}
                            </ContentWrapper>
                            {this.renderProductTabs(true, GROUP_01)}
                        </>
                    )}
                    {this.renderAutoRelatedSection()}
                    {this.renderProductTabs(false, GROUP_02)}
                    {this.renderStaticSection()}
                    {this.renderReviewPopup()}
                </main>
            </NoMatchHandler>
        );
    }
}

export default ProductPage;
